import { Button } from "react-bootstrap";
import { Form } from "src/components/common/form";
import { Input } from "src/components/common/form/input";
import { z } from "zod";
import { Select } from "src/components/common/form/select";
import { swalSuccess } from "src/utils/swalHelpers";
import { formatAttendanceDate } from "src/utils/formatdate";
import { QueryError } from "src/components/common/query-error";
import { QuerySpinner } from "src/components/common/query-spinner";
import {
  generatePayload,
  usePutAttendanceBusSchedules,
} from "src/services/react-query-apis/put-attendance-bus-schedules";
import { useAttendanceBusSchedulesQuery } from "src/services/react-query-apis/get-attendance-bus-schedules";
import { InputCheckbox } from "src/components/common/form/input-checkbox";
import { useFormContext } from "react-hook-form";

const busScheduleInputSchema = z
  .object({
    outbound_required: z.boolean(),
    bus_schedule_id: z.coerce.number().optional(),
    outbound_bus_route_id: z.coerce.number().optional(),
    outbound_extra_passengers: z.coerce.number().optional(),
    return_required: z.boolean(),
    return_bus_route_id: z.coerce.number().optional(),
    return_extra_passengers: z.coerce.number().optional(),
  })
  .refine(
    data => {
      if (
        (data.outbound_required || data.return_required) &&
        data.bus_schedule_id === 0
      ) {
        return false;
      }
      return true;
    },
    {
      message: "バスコースを選択してください",
      path: ["bus_schedule_id"],
    }
  )
  .refine(
    data => {
      if (data.outbound_required) {
        if (data.outbound_bus_route_id === 0) {
          return false;
        }
      }
      return true;
    },
    {
      message: "バス停を選択してください",
      path: ["outbound_bus_route_id"],
    }
  )
  .refine(
    data => {
      if (data.outbound_required) {
        if (
          data.outbound_extra_passengers > 2 ||
          data.outbound_extra_passengers < 0
        ) {
          return false;
        }
      }
      return true;
    },
    {
      message: "追加乗客数は0以上2以下にしてください",
      path: ["outbound_extra_passengers"],
    }
  )
  .refine(
    data => {
      if (data.return_required) {
        if (!data.return_bus_route_id || data.return_bus_route_id === 0) {
          return false;
        }
      }
      return true;
    },
    {
      message: "バス停を選択してください",
      path: ["return_bus_route_id"],
    }
  )
  .refine(
    data => {
      if (data.return_required) {
        if (
          data.return_extra_passengers > 2 ||
          data.return_extra_passengers < 0
        ) {
          return false;
        }
      }
      return true;
    },
    {
      message: "追加乗客数は0以上2以下にしてください",
      path: ["return_extra_passengers"],
    }
  );

export const BusForm = ({ attendance, onClose }) => {
  const mutationConfig = {
    onSuccess: async data => {
      await swalSuccess(data.message);
      onClose();
    },
  };

  const putAttendanceBusSchedulesMutation = usePutAttendanceBusSchedules({
    mutationConfig,
  });

  const attendanceBusSchedulesQuery = useAttendanceBusSchedulesQuery({
    attendanceId: attendance.id,
    queryConfig: {
      staleTime: 0,
    },
  });

  if (attendanceBusSchedulesQuery.isFetching) {
    return <QuerySpinner />;
  }

  if (attendanceBusSchedulesQuery.isError) {
    return <QueryError />;
  }

  const busSchedules = attendanceBusSchedulesQuery.data.data.bus_schedules;

  let form = (
    <Form
      onSubmit={values => {
        putAttendanceBusSchedulesMutation.mutate({
          data: generatePayload({
            attendanceId: attendance.id,
            schedule: values,
          }),
          attendanceId: attendance.id,
        });
      }}
      schema={busScheduleInputSchema}
      options={{
        defaultValues: {
          datetime: formatAttendanceDate(attendance),
          bus_schedule_id: attendance.bus_schedule_id || 0,
          outbound_required: busSchedules.length > 0,
          outbound_bus_route_id: attendance.outbound_bus_route_id || 0,
          outbound_extra_passengers:
            attendance.outbound_passengers > 0
              ? attendance.outbound_passengers - 1
              : 0,
          return_required: busSchedules.length > 0,
          return_bus_route_id: attendance.return_bus_route_id || 0,
          return_extra_passengers:
            attendance.return_passengers > 0
              ? attendance.return_passengers - 1
              : 0,
        },
      }}
    >
      {({ register, formState, watch }) => {
        const scheduleId = watch(`bus_schedule_id`);
        const schedule = busSchedules.find(s => s.id === Number(scheduleId));

        return (
          <>
            <Input
              disabled={true}
              type="text"
              label="対象授業"
              name="datetime"
              error={formState.errors["datetime"]}
              registration={register("datetime")}
              readOnly={true}
            />
            <BusScheduleSelection schedules={busSchedules} />
            {schedule && (
              <>
                <BusRouteSelection schedule={schedule} prefix="outbound" />
                <BusRouteSelection schedule={schedule} prefix="return" />
              </>
            )}
            <div style={{ textAlign: "end" }}>
              <Button
                disabled={putAttendanceBusSchedulesMutation.isPending}
                type="submit"
              >
                保存
              </Button>
            </div>
          </>
        );
      }}
    </Form>
  );

  return form;
};

const BusScheduleSelection = ({ schedules }) => {
  const { register, formState, setValue } = useFormContext();

  const scheduleOptions = [
    { value: 0, label: "バスコースを選択してください" },
    ...schedules.map(val => ({
      value: val.id,
      label: `${val.description}`,
    })),
  ];

  if (schedules.length === 0) {
    return <div className="text-md font-extrabold">バスコースがありません</div>;
  }

  return (
    <div className="space-y-2">
      <Select
        label={`バスコース`}
        name={`bus_schedule_id`}
        error={formState.errors[`bus_schedule_id`]}
        registration={register(`bus_schedule_id`)}
        options={scheduleOptions}
        onChange={e => {
          setValue(`bus_schedule_id`, e.target.value);
          setValue(`outbound_bus_route_id`, 0);
          setValue(`return_bus_route_id`, 0);
        }}
      />
    </div>
  );
};

const BusRouteSelection = ({ schedule, prefix }) => {
  const { watch, register, formState } = useFormContext();

  const required = watch(`${prefix}_required`);
  const scheduleId = Number(watch(`bus_schedule_id`));

  const routeOptions = [
    { value: 0, label: "バス停を選択してください" },
    ...schedule.bus_course.bus_routes.slice(1, -1).map(route => ({
      value: route.id,
      label: `${route.stop_name} (${route[`formatted_${prefix}_bus_stop_time`]})　(残席: ${route[`${prefix}_ridable_count`]})`,
    })),
  ];

  return (
    <div className="space-y-2">
      <InputCheckbox
        label={prefix === "outbound" ? "往路を利用する" : "復路を利用する"}
        name={`${prefix}_required`}
        registration={register(`${prefix}_required`)}
      />
      {required ? (
        <>
          {!!scheduleId && (
            <>
              <Select
                label={`${prefix === "outbound" ? "往路" : "復路"}バス停`}
                name={`${prefix}_bus_route_id`}
                error={formState.errors[`${prefix}_bus_route_id`]}
                registration={register(`${prefix}_bus_route_id`)}
                options={routeOptions}
              />
              <Input
                type="number"
                label={`${prefix === "outbound" ? "往路" : "復路"}追加乗客数`}
                name={`${prefix}_extra_passengers`}
                error={formState.errors[`${prefix}_extra_passengers`]}
                registration={register(`${prefix}_extra_passengers`)}
              />
            </>
          )}
        </>
      ) : (
        <div className="text-sm font-extrabold text-red-500">
          {prefix === "outbound" ? "往路" : "復路"}バスが欠席になります
        </div>
      )}
    </div>
  );
};
